// EPIC-P06 Story S-P06-01: Course Claims Admin Page
// Platform admin interface for approving/rejecting course claim requests

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, CheckCircle, XCircle, Clock, MapPin, FileText, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { callEdge } from '@/lib/edgeClient';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { SectionHero, AdminCard, StatGrid, MetricCard, StatusPill } from '@/components/admin/primitives';

interface CourseClaim {
  id: string;
  user_id: string;
  course_id: number;
  role: 'admin' | 'staff';
  verification_status: 'pending' | 'approved' | 'rejected';
  verification_document_url: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_at: string;
  course: {
    club_name: string;
    course_name: string;
    city: string;
    state: string;
  } | null;
  user: {
    email: string;
    full_name: string | null;
  } | null;
}

export default function CourseClaimsPage() {
  const { isPlatformAdmin, isLoading: adminLoading } = useAdminAccess();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');

  // Fetch course claims
  const { data: claims, isLoading, refetch } = useQuery<CourseClaim[]>({
    queryKey: ['course-claims', filter],
    queryFn: async () => {
      let query = supabase
        .from('course_admins')
        .select(`
          id,
          user_id,
          course_id,
          role,
          verification_status,
          verification_document_url,
          verified_by,
          verified_at,
          created_at
        `)
        .order('created_at', { ascending: false });

      if (filter === 'pending') {
        query = query.eq('verification_status', 'pending');
      }

      const { data: claimsData, error: claimsError } = await query;

      if (claimsError) {
        console.error('[CourseClaimsPage] Error fetching claims:', claimsError);
        throw claimsError;
      }

      if (!claimsData || claimsData.length === 0) {
        return [];
      }

      // Fetch courses separately
      const courseIds = [...new Set(claimsData.map(c => c.course_id))];
      const { data: courses } = await supabase
        .from('courses')
        .select('id, club_name, course_name, city, state')
        .in('id', courseIds);

      const courseMap = new Map<number, any>();
      (courses || []).forEach(c => {
        courseMap.set(c.id, c);
      });

      // Fetch user profiles separately
      const userIds = [...new Set(claimsData.map(c => c.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', userIds);

      const profileMap = new Map<string, string>();
      (profiles || []).forEach(p => {
        profileMap.set(p.user_id, p.full_name || 'Unknown');
      });

      return claimsData.map((claim: any) => ({
        ...claim,
        course: courseMap.get(claim.course_id) || null,
        user: {
          email: '', // Email not available from client-side query
          full_name: profileMap.get(claim.user_id) || null
        }
      }));
    },
    enabled: isPlatformAdmin && !adminLoading,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Approve/reject mutation
  const verifyClaim = useMutation({
    mutationFn: async ({ claimId, action }: { claimId: string; action: 'approve' | 'reject' }) => {
      return await callEdge('course-admin-verify', {
        method: 'POST',
        body: { course_admin_id: claimId, action },
        authRequired: true
      });
    },
    onSuccess: (data, variables) => {
      toast.success(`Claim ${variables.action === 'approve' ? 'approved' : 'rejected'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['course-claims'] });
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update claim');
    }
  });

  if (adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const pendingClaims = claims?.filter(c => c.verification_status === 'pending') || [];
  const approvedClaims = claims?.filter(c => c.verification_status === 'approved') || [];
  const rejectedClaims = claims?.filter(c => c.verification_status === 'rejected') || [];

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Course Claims"
          description="Review and approve course admin access requests"
          gradient="info"
          actions={
            <Button onClick={() => refetch()} disabled={isLoading} variant="outline" size="sm">
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          }
        />

        {/* Stats Cards */}
        <div className="mt-8">
          <StatGrid columns={3}>
            <MetricCard
              title="Pending"
              value={pendingClaims.length}
              size="large"
              accentColor="warning"
            />
            <MetricCard
              title="Approved"
              value={approvedClaims.length}
              size="large"
              accentColor="success"
            />
            <MetricCard
              title="Rejected"
              value={rejectedClaims.length}
              size="large"
              accentColor="danger"
            />
          </StatGrid>
        </div>

      {/* Claims List */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({pendingClaims.length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved ({approvedClaims.length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected ({rejectedClaims.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : pendingClaims.length === 0 ? (
            <AdminCard accentColor="info">
              <div className="py-12 text-center text-muted-foreground">
                No pending claims
              </div>
            </AdminCard>
          ) : (
            pendingClaims.map((claim) => (
              <AdminCard key={claim.id} accentColor="warning" title={claim.course?.club_name || 'Unknown Course'}>
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <p className="text-sm text-muted-foreground">
                      {claim.course?.city}, {claim.course?.state}
                    </p>
                    <StatusPill status="pending" label="Pending" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Requested by:</span>
                      <div className="font-medium mt-1">
                        {claim.user?.full_name || 'Unknown User'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {claim.user_id.substring(0, 8)}...
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Role:</span>
                      <div className="font-medium mt-1 capitalize">{claim.role}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Requested:</span>
                      <div className="font-medium mt-1">
                        {formatDistanceToNow(new Date(claim.created_at), { addSuffix: true })}
                      </div>
                    </div>
                    {claim.verification_document_url && (
                      <div>
                        <span className="text-muted-foreground">Document:</span>
                        <div className="mt-1">
                          <a
                            href={claim.verification_document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <FileText className="w-3 h-3" />
                            View Document
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      onClick={() => verifyClaim.mutate({ claimId: claim.id, action: 'approve' })}
                      disabled={verifyClaim.isPending}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      {verifyClaim.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => verifyClaim.mutate({ claimId: claim.id, action: 'reject' })}
                      disabled={verifyClaim.isPending}
                      variant="destructive"
                      size="sm"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </AdminCard>
            ))
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {approvedClaims.length === 0 ? (
            <AdminCard accentColor="info">
              <div className="py-12 text-center text-muted-foreground">
                No approved claims
              </div>
            </AdminCard>
          ) : (
            approvedClaims.map((claim) => (
              <AdminCard key={claim.id} accentColor="success">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">
                      {claim.course?.club_name || 'Unknown Course'}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {claim.course?.city}, {claim.course?.state}
                    </p>
                  </div>
                  <StatusPill status="approved" label="Approved" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Approved {claim.verified_at ? formatDistanceToNow(new Date(claim.verified_at), { addSuffix: true }) : 'recently'}
                </p>
              </AdminCard>
            ))
          )}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4">
          {rejectedClaims.length === 0 ? (
            <AdminCard accentColor="info">
              <div className="py-12 text-center text-muted-foreground">
                No rejected claims
              </div>
            </AdminCard>
          ) : (
            rejectedClaims.map((claim) => (
              <AdminCard key={claim.id} accentColor="danger">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">
                      {claim.course?.club_name || 'Unknown Course'}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {claim.course?.city}, {claim.course?.state}
                    </p>
                  </div>
                  <StatusPill status="rejected" label="Rejected" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Rejected {claim.verified_at ? formatDistanceToNow(new Date(claim.verified_at), { addSuffix: true }) : 'recently'}
                </p>
              </AdminCard>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
    </div>
  );
}

